import {
  Component,
  Host,
  h,
  Prop,
  Element,
  State,
  Method,
  Listen,
  Watch
} from "@stencil/core";

import {
  ChCheckboxCustomEvent,
  TabularGridRowContextMenuEvent
} from "@genexus/chameleon-controls-library";

import { getIconPath, MercuryBundles } from "@genexus/mercury";

import { Locale } from "../../common/locale";
import { GxOption } from "../../common/types";
import { EntityData, ContextMenuInfo } from "../../common/types";
import { config } from "../../common/config";
import { mapOptionsToComboBoxItemModel, hiChar } from "./helpers";
import {
  AccordionModel,
  ComboBoxModel
} from "@genexus/chameleon-controls-library";
import { formatDate } from "../../common/helpers";
import { GxIdeEntitySelectorCustomEvent } from "../..";

interface TabularGridWithSelection extends HTMLChTabularGridElement {
  selectAllRows: (select?: boolean) => Promise<void>;
}

const CSS_BUNDLES: MercuryBundles = [
  "resets/box-sizing",
  "components/button",
  "components/checkbox",
  "components/combo-box",
  "components/accordion",
  "components/edit",
  "components/icon",
  "components/tooltip",
  "components/tabular-grid",
  "utils/form",
  "utils/layout",
  "utils/typography",
  "utils/spacing"
];

const CLEAR_ICON = getIconPath({
  category: "system",
  name: "close",
  colorType: "interactive"
});
const FILTER_ICON = getIconPath({
  category: "window-tools",
  name: "filter",
  colorType: "on-surface"
});
const INFO_ICON = getIconPath({
  category: "system",
  name: "information",
  colorType: "on-surface"
});

const ACCORDION_FILTER_ID = "advanced-filter";
const FILTER_AFTER_DATE_TIME = "afterDateTime";
const FILTER_AFTER_LAST_BUILD = "afterLastBuild";
const FILTER_AFTER_IMPORT = "afterImport";

const CHECKBOX_CHECKED_VALUE = "checked";
const CHECKBOX_UNCHECKED_VALUE = "unchecked";

@Component({
  tag: "gx-ide-ww-files",
  styleUrl: "ww-files.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/ww-files"]
})
export class GxIdeWWFiles {
  #componentLocale: any;
  #ACCORDION_MODEL: AccordionModel = [];

  // Filter Values
  #nameValue: string;
  #moduleValue: EntityData;
  // Advanced Filter Values
  #searchContentsValue: string;
  #categoryValue: string;
  #allDescendants: CheckboxState;
  #userValue: string;
  // Other
  #selectedObjectsIds: string[] = [];

  @Element() el: HTMLGxIdeWwFilesElement;
  #chGridEl!: HTMLChTabularGridElement;

  /**
   * The "After" control value. Controls the visibility of the "Date/Time" control.
   */
  @State() afterValue: string;

  /**
   * The "Category" options as combo box model
   */
  @State() categoriesComboBoxModel: ComboBoxModel = [];

  /**
   * Value of the Date/Time control
   */
  @State() dateTimeValue: string;
  @Watch("dateTimeValue")
  dateTimeValueChanged() {
    this.#getObjects();
  }

  /**
   * A loader that is shown when the filter changes and the server has to retrieve new data.
   */
  @State() isLoading: boolean = true;

  /**
   * The options for the "After" combo-box
   */
  @State() filterAfterComboBoxModel: ComboBoxModel;

  /**
   * For show or hide the filters relative to modifiers
   */
  @State() showModified: CheckboxState = CHECKBOX_CHECKED_VALUE;

  /**
   * The files rendered in the table
   */
  @State() objects: ObjectData[] = [];

  /**
   * The categories render in the filter category selector
   */
  @Prop() readonly categories!: GxOption[];
  @Watch("categories")
  categoriesChanged(newCategories: GxOption[]) {
    this.categoriesComboBoxModel = mapOptionsToComboBoxItemModel(newCategories);
  }

  /**
   * This is a function provided by the developer for show the context menu of the selected files.
   */
  @Prop() readonly contextMenuCallback!: ContextMenuCallback;

  /**
   * The default value for the category filter
   */
  @Prop() readonly defaultCategory: string;

  /**
   * The default value for the module/folder filter
   */
  @Prop() readonly defaultModule: EntityData;

  /**
   * This is a function provided by the developer for delete the selected files.
   */
  @Prop() readonly deleteSelectionCallback!: DeleteSelectionCallback;

  /**
   * Controls the visibility of the tabular grid cell columns, and respective cells.
   */
  @Prop() readonly gridColumnsVisibility: GridColumnsVisibility = {
    description: true,
    importDate: true,
    lastBuildDate: true,
    lastUser: true,
    modifiedDate: true
  };

  /**
   *  This is a function provided by the developer that return a list of files to populate the table of files.
   */
  @Prop() readonly loadCallback!: LoadCallback;

  /**
   *
   */
  @Prop() readonly newFileCallback!: NewFileCallback;

  /**
   * This is a function provided by the developer to expand the data of files that is double-clicked or entered.
   */
  @Prop() readonly openSelectionCallback!: OpenSelectionCallback;

  /**
   * Callback invoked when the action is executed on the Module/Folder filter (button '...'). It returns the information of the selected object (id and name) or 'undefined' if it was canceled.
   */
  @Prop() readonly selectModuleCallback!: SelectModuleCallback;

  /**
   * This is a function provided by the developer for expanded the data of files that is double-clicked or entered.
   */
  @Prop() readonly selectionChangeCallback!: SelectionChangeCallback;

  /**
   * Controls whether to show the Search contents field in the advanced filter section (Not available at the time of writting, since the server is not handling this feature yet.)
   */
  @Prop() readonly showSearchContents = false;

  async componentWillLoad() {
    this.#componentLocale = await Locale.getComponentStrings(this.el);

    // initialize combo-box models∂
    this.categoriesChanged(this.categories);

    // initialize "After" combo-box options
    this.filterAfterComboBoxModel = [
      {
        caption: this.#componentLocale.filter[FILTER_AFTER_DATE_TIME],
        value: FILTER_AFTER_DATE_TIME
      },
      {
        caption: this.#componentLocale.filter[FILTER_AFTER_LAST_BUILD],
        value: FILTER_AFTER_LAST_BUILD
      },
      {
        caption: this.#componentLocale.filter[FILTER_AFTER_IMPORT],
        value: FILTER_AFTER_IMPORT
      }
    ];

    // initialize accordion model with proper configuration
    this.#ACCORDION_MODEL = [
      {
        id: ACCORDION_FILTER_ID,
        caption: this.#componentLocale.filter.accordionCaption,
        expanded: false
      }
    ];

    this.#setFilterDefaultValues();
  }
  #setFilterDefaultValues = () => {
    this.#nameValue = "";
    this.#moduleValue = { ...this.defaultModule };
    this.#searchContentsValue = "";

    this.#categoryValue =
      this.defaultCategory ||
      (this.categoriesComboBoxModel && this.categoriesComboBoxModel.length > 0
        ? this.categoriesComboBoxModel[0].value
        : "");

    this.#allDescendants = CHECKBOX_UNCHECKED_VALUE;

    this.afterValue =
      this.filterAfterComboBoxModel && this.filterAfterComboBoxModel.length > 0
        ? this.filterAfterComboBoxModel[0].value
        : FILTER_AFTER_DATE_TIME;

    this.#userValue = "";
    this.dateTimeValue = undefined;
  };

  componentDidLoad() {
    this.#attachGridListeners();
    this.#getObjects();
  }

  @Listen("keydown", { passive: true })
  keyDownHandler(eventInfo: KeyboardEvent) {
    if (document.activeElement === this.el) {
      switch (eventInfo.key) {
        case "Enter":
          eventInfo.preventDefault();
          this.#openSelectionCallbackHandler();
          break;
        case "Delete":
          eventInfo.preventDefault();
          this.#deleteSelectionCallbackHandler();
          break;
        case "Escape":
          eventInfo.preventDefault();
          this.#deselectAll();
          break;
      }
    }
  }

  /**
   * Reloads the view, refreshing the filters and the table of files.
   */
  @Method()
  async reload(): Promise<void> {
    try {
      if (this.#componentLocale && this.filterAfterComboBoxModel) {
        this.#setFilterDefaultValues();
        this.#getObjects();
      }
    } catch (error) {
      console.error("Error in reload method:");
      // Fallback: try only getObjects if setFilterDefaultValues fails
      try {
        this.#getObjects();
      } catch (fallbackError) {
        console.error("Fallback also failed:");
      }
    }
  }

  #allDescendantsCheckboxHandler = (
    event: ChCheckboxCustomEvent<any> | InputEvent
  ) => {
    this.#allDescendants =
      event.detail === CHECKBOX_CHECKED_VALUE
        ? CHECKBOX_CHECKED_VALUE
        : CHECKBOX_UNCHECKED_VALUE;
    this.#getObjects();
  };

  #handleSelectionChanged = (ev: CustomEvent<{ rowsId: string[] }>): void => {
    this.#selectedObjectsIds = ev.detail.rowsId;
    this.selectionChangeCallback(this.#selectedObjectsIds);
  };

  #handleRowDoubleClicked = (): void => {
    this.#openSelectionCallbackHandler();
  };

  #handleRowContextMenu = (
    ev: CustomEvent<TabularGridRowContextMenuEvent>
  ): void => {
    ev.preventDefault();
    ev.stopPropagation();
    this.contextMenuCallback({
      selection: ev.detail.selectedRowsId,
      clientX: ev.detail.clientX,
      clientY: ev.detail.clientY
    });
  };

  #attachGridListeners = (): void => {
    this.#chGridEl.addEventListener(
      "selectionChanged",
      this.#handleSelectionChanged
    );
    this.#chGridEl.addEventListener(
      "rowDoubleClicked",
      this.#handleRowDoubleClicked
    );
    this.#chGridEl.addEventListener(
      "rowContextMenu",
      this.#handleRowContextMenu
    );
  };

  #categoryInputHandler = async (event: CustomEvent<string> | InputEvent) => {
    this.#categoryValue = event.detail as string;
    this.#getObjects();
  };

  #clearDateTimeValue = () => {
    this.dateTimeValue = undefined;
  };

  #dateTimeInputHandler = (event: CustomEvent<any> | InputEvent) => {
    this.dateTimeValue = event.detail;
    this.#getObjects();
  };

  #deleteSelectionCallbackHandler = (): void => {
    this.deleteSelectionCallback(this.#selectedObjectsIds);
  };

  #deselectAll = (): void => {
    if (
      this.#chGridEl &&
      typeof (this.#chGridEl as TabularGridWithSelection).selectAllRows ===
        "function"
    ) {
      (this.#chGridEl as TabularGridWithSelection).selectAllRows(false);
    }
  };

  #evaluateModifiedDates = (
    objectModifiedDate: Date,
    filterSelectedModifiedDate: string
  ) => {
    const filterSelectedModifiedDateAsDate = new Date(
      filterSelectedModifiedDate
    );
    if (filterSelectedModifiedDateAsDate >= objectModifiedDate) {
      // Object modifed date matches with filter date, or was modified after that date.
      // Highlight to show that it matches.
      return <span class="hiChar">{this.#formatDate(objectModifiedDate)}</span>;
    } else {
      return this.#formatDate(objectModifiedDate);
    }
  };

  #filterAfterTypeCallbackHandler = (
    event: CustomEvent<string> | InputEvent
  ) => {
    this.afterValue = event.detail as string;
    this.#getObjects();
  };

  #formatDate = (date: Date): string => {
    return formatDate(date, "date-time-short");
  };

  #getObjects = () => {
    this.#setLoadingState();

    const parentId = this.#moduleValue ? this.#moduleValue.id : "";

    const filtersValues: ObjectFiltersData = {
      name: this.#nameValue,
      searchContents: this.#searchContentsValue,
      category: this.#categoryValue,
      parent: parentId,
      allDescendants: this.#allDescendants === CHECKBOX_CHECKED_VALUE,
      modifiedAfter: this.showModified ? this.afterValue : undefined,
      modifiedBy: this.showModified ? this.#userValue : undefined,
      modifiedDate: this.showModified ? new Date(this.dateTimeValue) : undefined
    };

    this.loadCallback(filtersValues).then((items: ObjectData[]) => {
      this.objects = items;
      this.#selectedObjectsIds = [];

      if (this.#chGridEl) {
        this.#deselectAll();
      }

      this.isLoading = false;
    });
  };

  #renderHeader = (): Element[] => {
    return [
      <div
        // Name
        class="field field-block field-name"
      >
        <label class="label" htmlFor="name">
          {this.#componentLocale.filter.name}
        </label>
        <ch-edit
          id="name"
          class="input"
          value={this.#nameValue}
          onInput={this.#nameInputHandler}
          part="filter-files"
          debounce={config.inputDebounce}
          disabled={this.isLoading}
        ></ch-edit>
      </div>,

      <div
        // Module
        class="field field-block field-module-folder"
      >
        <label class="label" htmlFor="module-folder">
          {this.#componentLocale.filter.moduleFolder}
        </label>
        <gx-ide-entity-selector
          part="filter-module-folder"
          id="module/folder"
          labelPosition="none"
          value={this.#moduleValue}
          defaultValue={this.defaultModule}
          selectEntityCallback={this.selectModuleCallback}
          onValueChanged={this.#moduleValueChangedHandler}
          disabled={this.isLoading}
        ></gx-ide-entity-selector>
      </div>,

      <ch-accordion-render
        class="accordion-outlined advanced-filter"
        model={this.#ACCORDION_MODEL}
      >
        <div
          slot={ACCORDION_FILTER_ID}
          class="spacing-body field-group advanced-filter__container"
        >
          {this.showSearchContents && (
            <div
              // Search contents
              class="field field-block field-search-contents"
            >
              <label class="label" htmlFor="filter-content">
                {this.#componentLocale.filter.content}
              </label>
              <ch-edit
                id="filter-content"
                class="input"
                onInput={this.#searchContentsInputHandler}
                part="filter-content"
              ></ch-edit>
            </div>
          )}

          <div class="field-group advanced-filter__first-row">
            <div
              // Category
              class="field field-block field-category"
            >
              <label class="label" htmlFor="filter-category">
                {this.#componentLocale.filter.category}
              </label>
              <ch-combo-box-render
                id="filter-category"
                class="combo-box disabled"
                value={this.#categoryValue}
                disabled={!this.categories || this.isLoading}
                model={this.categoriesComboBoxModel}
                part="filter-category"
                onInput={this.#categoryInputHandler}
              ></ch-combo-box-render>
            </div>

            <div
              // All descendants / Modified
              class="all-descendants__modified field field-inline"
            >
              <ch-checkbox
                // All descendants
                class="checkbox"
                caption={this.#componentLocale.filter.allDescendants}
                checkedValue={CHECKBOX_CHECKED_VALUE}
                unCheckedValue={CHECKBOX_UNCHECKED_VALUE}
                value={this.#allDescendants}
                onInput={this.#allDescendantsCheckboxHandler}
                part="filter-all-descendants"
                disabled={this.isLoading}
              ></ch-checkbox>

              <ch-checkbox
                // Modified
                class="checkbox align-center"
                caption={this.#componentLocale.filter.modified}
                checkedValue={CHECKBOX_CHECKED_VALUE}
                unCheckedValue={CHECKBOX_UNCHECKED_VALUE}
                value={this.showModified}
                onInput={this.#showModifiedCheckboxHandler}
                part="filter-modified"
              ></ch-checkbox>
            </div>
          </div>

          {this.showModified === CHECKBOX_CHECKED_VALUE && (
            <div class="field-group advanced-filter__second-row">
              <div
                // After
                class="field field-block field-after"
              >
                <label class="label" htmlFor="filter-after-type">
                  {this.#componentLocale.filter.after}
                </label>
                <ch-combo-box-render
                  id="filter-after-type"
                  class="combo-box"
                  value={this.afterValue}
                  model={this.filterAfterComboBoxModel}
                  onInput={this.#filterAfterTypeCallbackHandler}
                  part="filter-after-type"
                  disabled={this.isLoading}
                ></ch-combo-box-render>
              </div>

              <div
                // User
                class="field field-block field-user"
              >
                <label class="label" htmlFor="filter-user">
                  {this.#componentLocale.filter.user}
                </label>
                <ch-edit
                  id="filter-user"
                  class="input"
                  value={this.#userValue}
                  onInput={this.#userInputHandler}
                  part="filter-user"
                  debounce={config.inputDebounce}
                  disabled={this.isLoading}
                ></ch-edit>
              </div>

              {this.afterValue === FILTER_AFTER_DATE_TIME && (
                <div
                  // Date/Time
                  class={{
                    "field field-block field-date": true,
                    "hide": this.afterValue !== FILTER_AFTER_DATE_TIME
                  }}
                >
                  <label
                    class="label label-date-time"
                    htmlFor="filter-date-time"
                  >
                    {this.#componentLocale.filter.dateTime}
                    <ch-image
                      class="icon-s icon-clear-date-time"
                      src={INFO_ICON}
                    ></ch-image>
                    <ch-tooltip
                      class="tooltip"
                      actionElement={null}
                      blockAlign="outside-start"
                      inlineAlign="inside-start"
                      delay={config.tooltipSettings.delay}
                    >
                      {this.#componentLocale.filter.dateTimeTooltip}
                    </ch-tooltip>
                  </label>

                  <ch-edit
                    id="filter-date-time"
                    type="datetime-local"
                    class="input input-date-time"
                    value={this.dateTimeValue}
                    onInput={this.#dateTimeInputHandler}
                    part="filter-date-time"
                    disabled={this.isLoading}
                  ></ch-edit>

                  <button
                    class="button-tertiary button-icon-only"
                    onClick={this.#clearDateTimeValue}
                    disabled={this.isLoading}
                  >
                    <ch-image class="icon-m" src={CLEAR_ICON} type="mask" />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </ch-accordion-render>
    ];
  };

  #renderObjects = (): Element => {
    const gridIsEmpty = !this.objects.length;

    return (
      <ch-tabular-grid
        ref={(el: HTMLChTabularGridElement) => (this.#chGridEl = el)}
        class={{
          "tabular-grid": true,
          "loading": this.isLoading,
          "empty": this.objects.length === 0
        }}
        rowSelectionMode="multiple"
        part="ch-grid-files"
      >
        <ch-tabular-grid-columnset class="tabular-grid-column-set">
          <ch-tabular-grid-column
            columnName=""
            columnNameHidden={false}
            settingable={false}
            sortable={false}
            size={config.tabularGrid.colSize.maxContent}
            class="tabular-grid-column"
          ></ch-tabular-grid-column>

          <ch-tabular-grid-column
            columnName={this.#componentLocale.tableHead.name}
            columnNameHidden={false}
            settingable={false}
            sortable={false}
            size={config.tabularGrid.colSize.maxContent}
            class="tabular-grid-column"
          ></ch-tabular-grid-column>

          <ch-tabular-grid-column
            columnName={this.#componentLocale.tableHead.module}
            columnNameHidden={false}
            settingable={false}
            sortable={false}
            size={config.tabularGrid.colSize.maxContent}
            class="tabular-grid-column"
          ></ch-tabular-grid-column>

          {this.gridColumnsVisibility.description && (
            <ch-tabular-grid-column
              columnName={this.#componentLocale.tableHead.description}
              columnNameHidden={false}
              settingable={false}
              sortable={false}
              size={config.tabularGrid.colSize.auto}
              class="tabular-grid-column"
            ></ch-tabular-grid-column>
          )}

          {this.gridColumnsVisibility.modifiedDate && (
            <ch-tabular-grid-column
              columnName={this.#componentLocale.tableHead.modifiedDate}
              columnNameHidden={false}
              settingable={false}
              sortable={false}
              size={config.tabularGrid.colSize.maxContent}
              class="tabular-grid-column"
            ></ch-tabular-grid-column>
          )}

          {this.gridColumnsVisibility.lastUser && (
            <ch-tabular-grid-column
              columnName={this.#componentLocale.tableHead.lastUser}
              columnNameHidden={false}
              settingable={false}
              sortable={false}
              size={config.tabularGrid.colSize.maxContent}
              class="tabular-grid-column"
            ></ch-tabular-grid-column>
          )}

          {this.gridColumnsVisibility.importDate && (
            <ch-tabular-grid-column
              columnName={this.#componentLocale.tableHead.importDate}
              columnNameHidden={false}
              settingable={false}
              sortable={false}
              size={config.tabularGrid.colSize.maxContent}
              class="tabular-grid-column"
            ></ch-tabular-grid-column>
          )}

          {this.gridColumnsVisibility.lastBuildDate && (
            <ch-tabular-grid-column
              columnName={this.#componentLocale.tableHead.lastBuildDate}
              columnNameHidden={false}
              settingable={false}
              sortable={false}
              size={config.tabularGrid.colSize.maxContent}
              class="tabular-grid-column"
            ></ch-tabular-grid-column>
          )}
        </ch-tabular-grid-columnset>

        {!gridIsEmpty ? (
          <ch-tabular-grid-rowset class="tabular-grid-rowset">
            {this.objects?.map((object: ObjectData) => (
              <ch-tabular-grid-row rowid={object.id} class="tabular-grid-row">
                <ch-tabular-grid-cell class="tabular-grid-cell">
                  <ch-image src={object.icon}></ch-image>
                </ch-tabular-grid-cell>

                <ch-tabular-grid-cell class="tabular-grid-cell">
                  {hiChar(
                    object.name.toLowerCase(),
                    this.#nameValue.toLowerCase()
                  )}
                </ch-tabular-grid-cell>

                <ch-tabular-grid-cell class="tabular-grid-cell">
                  {object.parent}
                </ch-tabular-grid-cell>

                {this.gridColumnsVisibility.description && (
                  <ch-tabular-grid-cell class="tabular-grid-cell tabular-grid-cell-description">
                    {object.description}
                  </ch-tabular-grid-cell>
                )}

                {this.gridColumnsVisibility.modifiedDate && (
                  <ch-tabular-grid-cell class="tabular-grid-cell">
                    {this.#evaluateModifiedDates(
                      object.modifiedDate,
                      this.dateTimeValue
                    )}
                  </ch-tabular-grid-cell>
                )}

                {this.gridColumnsVisibility.lastUser && (
                  <ch-tabular-grid-cell class="tabular-grid-cell">
                    {hiChar(object.lastUser?.toLowerCase(), this.#userValue)}
                  </ch-tabular-grid-cell>
                )}

                {this.gridColumnsVisibility.importDate && (
                  <ch-tabular-grid-cell class="tabular-grid-cell">
                    {hiChar(
                      object.importDate
                        ? this.#formatDate(object.importDate)
                        : "",
                      this.#nameValue
                    )}
                  </ch-tabular-grid-cell>
                )}

                {this.gridColumnsVisibility.lastBuildDate && (
                  <ch-tabular-grid-cell class="tabular-grid-cell">
                    {hiChar(
                      object.lastBuildDate
                        ? this.#formatDate(object.lastBuildDate)
                        : "",
                      this.#nameValue
                    )}
                  </ch-tabular-grid-cell>
                )}
              </ch-tabular-grid-row>
            ))}
          </ch-tabular-grid-rowset>
        ) : (
          <ch-tabular-grid-rowset class="tabular-grid-rowset">
            <ch-tabular-grid-rowset-empty>
              {this.isLoading ? (
                <gx-ide-loader
                  loaderTitle={this.#componentLocale.loader.title}
                  description={this.#componentLocale.loader.description}
                  show
                ></gx-ide-loader>
              ) : (
                <gx-ide-empty-state
                  isAnimated={true}
                  stateIconSrc={FILTER_ICON}
                  stateTitle={this.#componentLocale.emptyState.title}
                ></gx-ide-empty-state>
              )}
            </ch-tabular-grid-rowset-empty>
          </ch-tabular-grid-rowset>
        )}
      </ch-tabular-grid>
    );
  };

  #moduleValueChangedHandler = (
    event: GxIdeEntitySelectorCustomEvent<EntityData>
  ) => {
    this.#moduleValue = event.detail;
    this.#getObjects();
  };

  #nameInputHandler = (event: CustomEvent<any> | InputEvent) => {
    this.#nameValue = event.detail;
    this.#getObjects();
  };

  #newFileCallbackHandler = (): void => {
    this.newFileCallback();
  };

  #openSelectionCallbackHandler = (): void => {
    this.openSelectionCallback(this.#selectedObjectsIds);
  };

  #searchContentsInputHandler = (event: CustomEvent<any> | InputEvent) => {
    this.#searchContentsValue = event.detail;
    this.#getObjects();
  };

  #setLoadingState = () => {
    // Only clear if no objects or first load
    if (!this.objects || this.objects.length === 0) {
      this.objects = [];
    }
    this.isLoading = true;
  };

  #userInputHandler = (event: CustomEvent<any> | InputEvent) => {
    this.#userValue = event.detail;
    this.#getObjects();
  };

  #showModifiedCheckboxHandler = (
    event: ChCheckboxCustomEvent<any> | InputEvent
  ) => {
    this.showModified = event.detail;
  };

  #selectAll = (): void => {
    if (
      this.#chGridEl &&
      typeof (this.#chGridEl as TabularGridWithSelection).selectAllRows ===
        "function"
    ) {
      (this.#chGridEl as TabularGridWithSelection).selectAllRows();
    }
  };

  render(): void {
    return (
      <Host class="widget">
        <ch-theme model={CSS_BUNDLES}></ch-theme>

        <section class="section">
          <header class="header dialog-header-with-border spacing-body-inline spacing-body-block-start field-group">
            {this.#renderHeader()}
          </header>

          {this.#renderObjects()}

          <footer class="dialog-footer-with-border dialog-footer-space-between spacing-body">
            <p
              // matching objects
              class="text-body"
              part="objects-matching"
            >
              {`${this.objects?.length} ${
                this.#componentLocale.objCount.matching
              }`}
            </p>

            <div class="buttons-spacer">
              <button
                // select all button
                id="button-select-all"
                class="button-secondary"
                onClick={this.#selectAll}
                part="button button--select-all"
                disabled={this.isLoading}
              >
                {this.#componentLocale.footer.btnSelectAll}
              </button>

              <button
                // new file button
                id="button-new-file"
                class="button-primary button-icon-and-text"
                onClick={this.#newFileCallbackHandler}
                part="button button--new-file"
                disabled={this.isLoading}
              >
                {this.#componentLocale.footer.btnNew}
              </button>
            </div>
          </footer>
        </section>
      </Host>
    );
  }
}

export type SelectionChangeCallback = (ids: string[]) => Promise<void>;
export type OpenSelectionCallback = (ids: string[]) => Promise<void>;
export type DeleteSelectionCallback = (ids: string[]) => Promise<void>;
export type SelectModuleCallback = () => Promise<EntityData | undefined>;
// export type ContextMenuCallback = (ids: string[]) => Promise<void>;
export type ContextMenuCallback = (
  contextMenuInfo: ContextMenuInfo
) => Promise<void>;
export type NewFileCallback = () => Promise<void>;
export type LoadCallback = (
  filters: ObjectFiltersData
) => Promise<ObjectData[]>;

export interface ObjectFiltersData {
  name?: string;
  searchContents?: string;
  category?: string;
  parent?: string;
  allDescendants?: boolean;
  modifiedAfter?: string;
  modifiedBy?: string;
  modifiedDate?: Date;
}
export interface ObjectData {
  id: string;
  icon: string;
  name: string;
  description: string;
  parent: string;
  modifiedDate?: Date;
  lastUser?: string;
  importDate?: Date;
  lastBuildDate: Date;
}

export type GridColumnsVisibility = {
  description: boolean;
  importDate: boolean;
  lastBuildDate: boolean;
  lastUser: boolean;
  modifiedDate: boolean;
};

type CheckboxState =
  | typeof CHECKBOX_CHECKED_VALUE
  | typeof CHECKBOX_UNCHECKED_VALUE;
